import React, { forwardRef, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Slide,
    Typography,
    IconButton,
    Box,
    Checkbox,
    CircularProgress,
    Tooltip,
    FormControlLabel,
    Switch,
    Divider,
    TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getBGVPendingCandidates } from './preboardingApi';
import { useParams } from 'react-router-dom';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { theme } from '../../../theme';
import ReactQuill from 'react-quill';
import { emailTemplate } from '../CandidateManagement/CandidateContent/CandidateContent';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { removeSpacing, sanitizeContent } from '../../Common/Sanitize/sanitizeReactQuill';


interface UpdateBGVStatusProps {
    isOpen: boolean;
    onClose: () => void;
    handleUpdateBgvStatus: (selectedCandidates: any) => boolean | Promise<boolean>;
    emailData: emailTemplate[]
}


interface Candidate {
    CandidateName: string;
    CandidateEmail: string;
    MapID: number;
    JobTitle: string;
    StatusKey: string;
    StatusId: number;
    StatusName: string;
    CandidateNum: number;
    JobNum: number;
    RecruiterName: string;
}


const UpdateBGVStatus: React.FC<UpdateBGVStatusProps> = ({
    isOpen,
    onClose,
    handleUpdateBgvStatus,
    emailData
}) => {

    const [selectedCandidates, setSelectedCandidates] = useState<{ MapID: number, EmailID: string, CandidateName: string, JobNum: number, CandidateNum: number, RecruiterName: string, JobTitle: string }[]>([]);
    const [candidates, setCandidates] = React.useState<Candidate[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const { tenant } = useParams<{ tenant: string }>();
    const [templateData, setTemplateData] = useState({ emailSubject: '', emailBody: '', emailNum: 0 })
    const { showSnackbar } = useSnackbar();
    const [isBgvCleared, setIsBgvCleared] = useState(true); // Add this at the top with other useState hooks

    const fetchBgvPendingCandidates = async () => {
        // This function would typically fetch the candidates from an API
        try {
            setIsLoading(true);
            const candidates = await getBGVPendingCandidates(tenant);
            setCandidates(candidates);
        } catch (error) {
            console.error("Error fetching BGV pending candidates:", error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isOpen) {
            fetchBgvPendingCandidates();
        }
        setSelectedCandidates([]);
        setIsBgvCleared(true);
    }, [tenant, isOpen]);

    useEffect(() => {
        if (isOpen && emailData.length > 0) {
            const key = isBgvCleared ? 'BGV Cleared' : 'BGV Rejected';
            const BGVTemplate = emailData.find((email) => email.EmailKey === key);
            if (BGVTemplate) {
                setTemplateData({
                    emailNum: BGVTemplate.EmailNum,
                    emailSubject: BGVTemplate.EmailSubject,
                    emailBody: BGVTemplate.EmailBody
                });
            }
        }
    }, [isOpen, emailData, isBgvCleared]);



    const handleSelectedCandidates = (MapID: number, EmailID: string, CandidateName: string, JobNum: number, CandidateNum: number, RecruiterName: string, JobTitle: string) => {
        if (selectedCandidates.some(c => c.MapID === MapID)) {
            setSelectedCandidates(prev => prev.filter(c => c.MapID !== MapID));
        } else {
            setSelectedCandidates(prev => [...prev, { MapID, EmailID, CandidateName, JobNum, CandidateNum, RecruiterName, JobTitle }]);
        }
    }

    // ✅ handlers for subject and body
    const handleEmailSubjectChange = (value: string) => {
        setTemplateData(prev => ({ ...prev, emailSubject: value }));
    };

    const handleEmailBodyChange = (value: string) => {
        setTemplateData(prev => ({ ...prev, emailBody: value }));
    };

    const handleSubmit = async () => {
        if (selectedCandidates.length === 0) {
            showSnackbar("Please select at least one candidate.", "error");
            return;
        }

        if (!templateData.emailSubject.trim()) {
            showSnackbar("Email subject is required.", "error");
            return;
        }

        const body = sanitizeContent(templateData.emailBody);
        if (!body) {
            showSnackbar("Email body is required.", "error");
            return;
        }
        const payLoad = {
            CandidateList: selectedCandidates,
            PreboardingStatusKey: "BGVPending",
            // IsHiringExperienceNeeded: wantHiringExperience,
            EmailBody: removeSpacing(templateData.emailBody),
            EmailSubject: templateData.emailSubject,
            EmailNum: templateData.emailNum,
            IsBGVCleared: isBgvCleared
        }
        const result = await handleUpdateBgvStatus(payLoad);
        if (result) {
            setSelectedCandidates([]);
            onClose();
        }
    }

    return (
        <Dialog
            open={isOpen}
            onClose={() => { }}
            disableEscapeKeyDown
            hideBackdrop={false}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.3) !important',
                },
                onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                },
            }}
            PaperProps={{
                sx: {
                    maxWidth: '60.2vw',
                    maxHeight: '44.8vw',
                    width: '100%',
                    height: '100%'
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'start', justifyContent: "space-between" }}>
                <Box>
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ flexGrow: 1, color: theme.palette.text.primary, fontWeight: 'bold' }}
                    >
                        Update Background Verification
                    </Typography>

                    <Typography variant='subtitle1'>
                        Set Candidates as Hired After Verification Completion or Rejected (if any discrepancies found)
                    </Typography>
                </Box>

                <IconButton
                    edge="end"
                    color="inherit"
                    sx={{ position: 'relative', width: '2vw', height: '4vh' }}
                    onClick={onClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    gap: 5,
                    minHeight: "50vh", // keep height consistent
                    justifyContent: candidates.length === 0 && !isLoading ? "center" : "flex-start"
                }}
            >
                {/* ✅ Loading */}
                {isLoading && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%"
                        }}
                    >
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Loading candidates...
                        </Typography>
                    </Box>
                )}

                {/* ✅ No candidates */}
                {!isLoading && candidates.length === 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                            textAlign: "center",
                            flexDirection: 'column'
                        }}
                    >
                        <RemoveCircleOutlineOutlinedIcon fontSize='large' color='primary' sx={{ fontSize: '3.5vw' }} />
                        <Typography variant="body1" color="text.secondary">
                            No BGV pending candidates available.
                        </Typography>
                    </Box>
                )}

                {/* ✅ Candidate list + email editor */}
                {!isLoading && candidates.length > 0 && (
                    <>
                        {/* Left panel: candidate list */}
                        <Box
                            sx={{
                                maxHeight: "70vh",
                                width: "20vw",
                                maxWidth: "30vw",
                                overflowY: "auto",
                                scrollbarWidth: "thin",
                                scrollBehavior: "smooth",
                            }}
                        >
                            {candidates.map((candidate, index) => (
                                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                                    <Checkbox
                                        checked={selectedCandidates.some(c => c.MapID === candidate.MapID)}
                                        onChange={() => {
                                            handleSelectedCandidates(
                                                candidate.MapID,
                                                candidate.CandidateEmail,
                                                candidate.CandidateName,
                                                candidate.JobNum,
                                                candidate.CandidateNum,
                                                candidate.RecruiterName,
                                                candidate.JobTitle
                                            );
                                        }}
                                    />
                                    <Typography variant="body1">{candidate.CandidateName}</Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Right panel: email editor */}
                        <Box sx={{ display: "flex", flexDirection: "column", width: "40vw" }}>
                            <Typography variant="subtitle1" mb={1}>
                                Fields marked with ‘@’ are system-generated placeholders and should not be edited.
                            </Typography>

                            <TextField
                                label="Subject"
                                size="small"
                                value={templateData.emailSubject}
                                fullWidth
                                margin="dense"
                                required
                                onChange={(e) => handleEmailSubjectChange(e.target.value)}
                            />

                            <Typography variant="caption" sx={{ mb: 0, mt: 1 }}>
                                Email Body<span style={{ color: theme.palette.error.main }}>* </span>
                            </Typography>

                            <ReactQuill
                                value={templateData.emailBody}
                                onChange={handleEmailBodyChange}
                                style={{ height: "30vh", marginTop: "1vh", marginBottom: "2.5vw" }}
                                modules={{
                                    toolbar: [["bold", "italic", "link", { list: "ordered" }, { list: "bullet" }]]
                                }}
                            />
                        </Box>
                    </>
                )}
            </DialogContent>


            {
                candidates.length > 0 && (
                    <DialogActions
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: "flex-end",
                            mr: 2.5,
                            mb: 1.3,
                        }}>
                        {/* <Tooltip title="Enable this if you want to collect hiring experience feedback from the candidate." slotProps={{
                            popper: {
                                sx: {
                                    "& .MuiTooltip-tooltip": {
                                        backgroundColor: "#fff",
                                        color: "#000",
                                        fontSize: "0.75rem",
                                        border: "1px solid #ccc",
                                    },
                                    maxWidth: '17vw'
                                },
                            },
                        }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={wantHiringExperience}
                                        onChange={(_, checked) => setWantHiringExperience(checked)}
                                        color="primary"
                                    />
                                }
                                label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Collect hiring feedback</Typography>}
                            />
                        </Tooltip> */}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: !isBgvCleared ? theme.palette.error.main : theme.palette.text.primary
                                }}
                            >
                                Rejected
                            </Typography>
                            <Switch
                                checked={isBgvCleared}
                                onChange={(_, checked) => setIsBgvCleared(checked)}
                                color={isBgvCleared ? "primary" : "error"}
                                sx={
                                    !isBgvCleared
                                        ? {
                                            '& .MuiSwitch-thumb': {
                                                bgcolor: '#B42318', // Red thumb
                                            },
                                            '& .MuiSwitch-track': {
                                                bgcolor: '#FEE4E2', // Light red track
                                                opacity: 1,
                                            },
                                        }
                                        : {}
                                }
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: isBgvCleared ? theme.palette.primary.main : theme.palette.text.primary
                                }}
                            >
                                Cleared
                            </Typography>
                        </Box>


                        <Button
                            variant={isBgvCleared ? "contained" : "outlined"}
                            color={isBgvCleared ? "primary" : "error"}
                            sx={
                                !isBgvCleared
                                    ? { ml: 2, borderColor: '#B42318', color: '#B42318', '&:hover': { bgcolor: '#B42318', color: '#fff' } }
                                    : { ml: 2 }
                            }
                            disabled={selectedCandidates.length === 0}
                            onClick={handleSubmit}
                        >
                            {isBgvCleared ? "BGV Cleared" : "BGV Rejected"}
                        </Button>

                    </DialogActions>
                )
            }


        </Dialog>
    );
};

export default UpdateBGVStatus;